import { Divider, Flex, Grid, Link, Menu, Navbar, Text, Tooltip } from '@lego/klik-ui';
import { ChevronDownSolid, MenuNavigationVertical } from '@lego/klik-ui/icons';
import React from 'react';
import { matchPath, Link as ReactRouterLink, useLocation } from 'react-router-dom';

type NavBarItem = {
  path: string;
  label: string;
};

type ActionsNavBarItem = {
  label: string;
  icon: React.JSX.Element;
  onClick: () => void;
  disabled?: boolean;
  tooltip?: string;
  color?: string;
  dividerBefore?: boolean;
};

type Props = {
  options: NavBarItem[];
  actions?: {
    header: string;
    items: ActionsNavBarItem[];
  };
};

export const SubNavigationBar = (props: Props) => {
  const { pathname } = useLocation();
  const isActive = (path: string) => matchPath(path, pathname) !== null;

  return (
    <Grid.Item as="header" gridArea="mainNavbar" my={4} width="100%">
      <Navbar
        boxShadow={'md'}
        isFullWidth={false}
        isSecondary={true}
        justifyContent={{ base: 'space-around', md: 'flex-start' }}
        p={'unset'}
        rounded={{ base: 'none', md: 'md' }}
      >
        <Navbar.Segment display={{ base: 'none', lg: 'flex' }} overflowX={'auto'}>
          {props.options.map((option) => (
            <Navbar.Item
              as={ReactRouterLink}
              isActive={isActive(option.path)}
              key={option.path}
              mx={2}
              p={0}
              textColor={isActive(option.path) ? 'slate.900' : 'none'}
              to={option.path}
            >
              {option.label}
            </Navbar.Item>
          ))}
        </Navbar.Segment>

        <Navbar.Segment display={{ base: 'flex', lg: 'none' }} justifyContent={'flex-start'} px={2}>
          <Menu placement="bottom-start">
            <Menu.Button
              alignSelf="center"
              as={Link}
              fontSize={'2xl'}
              mx={5}
              tabIndex={0}
              textAlign={'center'}
            >
              <MenuNavigationVertical />
            </Menu.Button>
            <Menu.List display={{ base: 'block', lg: 'none' }} justifyContent={'left'}>
              {props.options.map((option) => (
                <Navbar.Item
                  as={ReactRouterLink}
                  backgroundColor={isActive(option.path) ? 'slate.200' : 'none'}
                  key={option.path}
                  p={3}
                  textColor={isActive(option.path) ? 'black' : 'slate.600'}
                  to={option.path}
                >
                  {option.label}
                </Navbar.Item>
              ))}
            </Menu.List>
          </Menu>
        </Navbar.Segment>
        {props.actions && (
          <Menu>
            <Menu.Button as={Link} mx={2} px={3} tabIndex={0} textColor="slate.600">
              <Text>
                {props.actions.header} <ChevronDownSolid ml={2} />
              </Text>
            </Menu.Button>
            <Menu.List>
              {props.actions.items.map((item) => (
                <React.Fragment key={item.label}>
                  {item.dividerBefore && <Divider />}
                  <Tooltip isDisabled={!item.tooltip} label={item.tooltip} placement="right">
                    <Menu.Item isDisabled={item.disabled} onClick={item.onClick}>
                      <Flex align="center" gap="2">
                        <Flex color={item.color}>{item.icon}</Flex>
                        {item.label}
                      </Flex>
                    </Menu.Item>
                  </Tooltip>
                </React.Fragment>
              ))}
            </Menu.List>
          </Menu>
        )}
      </Navbar>
    </Grid.Item>
  );
};
